/**
 * Brazilian document numbers: CPF (people) and CNPJ (companies).
 *
 * A stored document is NORMALIZED: no punctuation, upper case. A CPF is 11 digits. A CNPJ is
 * 14 characters: the first 12 are digits or upper-case letters (the alphanumeric CNPJ the
 * federal revenue issues from July 2026 on; the numeric ones stay valid) and the last 2 are
 * numeric check digits. Each character weighs its ASCII code minus 48 — `0`–`9` are worth
 * 0–9 and `A`–`Z` 17–42 — so a numeric CNPJ checks exactly as it always did.
 *
 * A number made of one repeated character is refused, even when its check digits add up.
 */

export const CPF_LENGTH = 11;
export const CNPJ_LENGTH = 14;

/** A normalized CPF: 11 digits. */
export const CPF_PATTERN = /^\d{11}$/;
/** A normalized CNPJ: 12 digits or upper-case letters, then 2 numeric check digits. */
export const CNPJ_PATTERN = /^[0-9A-Z]{12}\d{2}$/;
/** What a normalized document may hold: digits and upper-case letters only. */
export const DOCUMENT_CHARACTERS = /^[0-9A-Z]+$/;

export type DocumentKind = 'cpf' | 'cnpj';

const CPF_WEIGHTS_1 = [10, 9, 8, 7, 6, 5, 4, 3, 2];
const CPF_WEIGHTS_2 = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_WEIGHTS_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_WEIGHTS_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/** `"12.abc.345/01de-35"` → `"12ABC34501DE35"`: upper case, letters and digits only. */
export function normalizeDocument(value: string | null | undefined): string {
  return (value ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '');
}

/** The value of one character in the check digit sum: its ASCII code minus 48. */
function characterValue(character: string): number {
  return character.charCodeAt(0) - 48;
}

/** The check digit of `characters` weighted by `weights` (mod 11; remainders 0 and 1 → 0). */
function checkDigit(characters: string, weights: readonly number[]): number {
  const sum = weights.reduce(
    (total, weight, index) => total + weight * characterValue(characters[index] ?? '0'),
    0,
  );
  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
}

const repeated = (characters: string) => /^(.)\1*$/.test(characters);

export function isValidCpf(value: string | null | undefined): boolean {
  const cpf = value ?? '';
  if (!CPF_PATTERN.test(cpf) || repeated(cpf)) return false;
  return (
    checkDigit(cpf, CPF_WEIGHTS_1) === Number(cpf[9]) &&
    checkDigit(cpf, CPF_WEIGHTS_2) === Number(cpf[10])
  );
}

/** A normalized CNPJ, numeric or alphanumeric, whose check digits add up. */
export function isValidCnpj(value: string | null | undefined): boolean {
  const cnpj = value ?? '';
  if (!CNPJ_PATTERN.test(cnpj) || repeated(cnpj)) return false;
  return (
    checkDigit(cnpj, CNPJ_WEIGHTS_1) === Number(cnpj[12]) &&
    checkDigit(cnpj, CNPJ_WEIGHTS_2) === Number(cnpj[13])
  );
}

export function isValidDocument(kind: DocumentKind, value: string | null | undefined): boolean {
  return kind === 'cpf' ? isValidCpf(value) : isValidCnpj(value);
}

/**
 * Completes a base with its check digits: 9 digits → a CPF; 12 digits or letters → a CNPJ.
 * Throws on any other base.
 */
export function withCheckDigits(base: string): string {
  const characters = normalizeDocument(base);
  let weights: [readonly number[], readonly number[]] | null = null;
  if (characters.length === CPF_LENGTH - 2 && /^\d+$/.test(characters)) {
    weights = [CPF_WEIGHTS_1, CPF_WEIGHTS_2];
  } else if (characters.length === CNPJ_LENGTH - 2) {
    weights = [CNPJ_WEIGHTS_1, CNPJ_WEIGHTS_2];
  }
  if (!weights) {
    throw new Error(`Expected a CPF base of 9 digits or a CNPJ base of 12 characters.`);
  }
  const withFirst = `${characters}${checkDigit(characters, weights[0])}`;
  return `${withFirst}${checkDigit(withFirst, weights[1])}`;
}

/** `"52998224725"` → `"529.982.247-25"`; anything that is not 11 characters comes back as is. */
export function formatCpf(value: string | null | undefined): string {
  const cpf = normalizeDocument(value);
  if (cpf.length !== CPF_LENGTH) return value ?? '';
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
}

/** `"12ABC34501DE35"` → `"12.ABC.345/01DE-35"`; anything that is not 14 characters as is. */
export function formatCnpj(value: string | null | undefined): string {
  const cnpj = normalizeDocument(value);
  if (cnpj.length !== CNPJ_LENGTH) return value ?? '';
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
}

/**
 * A CPF with only the middle digits showing, for records that outlive the person's data
 * (the audit log): `"52998224725"` → `"***.982.247-**"`. Anything else comes back masked
 * whole (`"***"`), never in clear.
 */
export function maskCpf(value: string | null | undefined): string {
  const cpf = normalizeDocument(value);
  if (cpf.length !== CPF_LENGTH) return '***';
  return `***.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-**`;
}
